import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ImportProgressRow = {
  id: string;
  user_id: string;
  bank_account_id: string;
  file_name: string;

  status: string;
  message: string | null;

  total_rows: number;
  processed_rows: number;
  parsed_count: number;
  inserted_count: number;
  duplicates_in_file: number;
  duplicates_in_db: number;

  updated_at: string;
};

const isTerminal = (s: string) => {
  const t = (s || "").toLowerCase();
  return t === "done" || t === "error";
};

export function useImportProgress(progressId: string | null) {
  const [row, setRow] = useState<ImportProgressRow | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!progressId) {
      setRow(null);
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }

    let active = true;

    const fetchOnce = async () => {
      const { data, error } = await supabase
        .from("bank_statement_import_progress")
        .select("*")
        .eq("id", progressId)
        .single();

      if (!active) return;
      if (error) {
        console.error("Failed to load progress:", error);
        return;
      }
      setRow(data as any);
    };

    // 1) load immediately
    fetchOnce();

    // 2) realtime updates
    const channel = supabase
      .channel(`import-progress:${progressId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "bank_statement_import_progress",
          filter: `id=eq.${progressId}`,
        },
        (payload) => setRow(payload.new as any),
      )
      .subscribe();

    // 3) polling fallback (каждые 800мс) пока не done/error
    pollRef.current = window.setInterval(() => {
      const s = (row?.status || "").toLowerCase();
      if (isTerminal(s)) return;
      fetchOnce();
    }, 800);

    return () => {
      active = false;
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressId]);

  // отдельный эффект: как только row дошёл до done/error — выключаем polling
  useEffect(() => {
    const s = (row?.status || "").toLowerCase();
    if (pollRef.current && isTerminal(s)) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [row?.status]);

  return { row };
}
